import { formatPrice } from '../utils/formatPrice';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';

const styles = {
  page: { minHeight: '100vh', background: '#f4f1eb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: 'system-ui, sans-serif' },
  card: { background: '#fff', borderRadius: '16px', padding: '2.5rem', width: '100%', maxWidth: '560px', border: '1px solid rgba(0,0,0,0.08)' },
  logo: { fontFamily: 'Georgia, serif', fontSize: '18px', color: '#1a1a1a', textDecoration: 'none', display: 'block', marginBottom: '1.5rem' },
  logoAccent: { color: '#1a6b4a' },
  progressBar: { display: 'flex', gap: '6px', marginBottom: '0.5rem' },
  progressStep: { flex: 1, height: '4px', borderRadius: '99px', transition: 'background 0.3s' },
  stepLabel: { fontSize: '12px', color: '#6b6b6b', marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
  title: { fontSize: '22px', fontWeight: '500', color: '#1a1a1a', marginBottom: '0.5rem' },
  sub: { fontSize: '14px', color: '#6b6b6b', marginBottom: '1.5rem', lineHeight: '1.6' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', marginBottom: '1rem' },
  field: { marginBottom: '1rem', position: 'relative' },
  label: { display: 'block', fontSize: '12px', fontWeight: '500', color: '#6b6b6b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' },
  fieldNote: { fontSize: '11px', color: '#9b9b9b', marginTop: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid rgba(0,0,0,0.12)', borderRadius: '8px', fontSize: '15px', color: '#1a1a1a', outline: 'none', boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif', background: '#fff' },
  btn: { width: '100%', padding: '12px', background: '#1a6b4a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: 'pointer', marginTop: '1rem' },
  addBtn: { background: 'transparent', border: '1px dashed rgba(0,0,0,0.2)', color: '#6b6b6b', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '0.5rem' },
  removeBtn: { background: 'transparent', border: 'none', color: '#dc2626', fontSize: '13px', cursor: 'pointer', padding: '8px 0' },
  holdingRow: { background: '#f9f9f7', borderRadius: '10px', padding: '1rem', marginBottom: '0.75rem' },
  holdingGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", alignItems: "end" },
  successIcon: { width: '60px', height: '60px', borderRadius: '50%', background: '#e8f5ef', color: '#1a6b4a', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid rgba(0,0,0,0.12)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '240px', overflowY: 'auto' },
  dropdownItem: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: '14px' },
  dropdownSymbol: { fontWeight: '500', color: '#1a1a1a', minWidth: '50px' },
  dropdownName: { color: '#6b6b6b', flex: 1 },
  dropdownPrice: { display: 'none' }
};

const STEPS = ['Your holdings', 'Your goals', 'Your exit plan', 'Your tax situation', 'All set'];

function CoinSearch({ value, onChange, onSelect }) {
  const [query, setQuery] = useState(value || '');
  const [coins, setCoins] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    axios.get(`${API}/api/prices/list`)
      .then(res => setCoins(res.data.coins || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (query.length === 0) {
      setFiltered([]);
      setOpen(false);
      return;
    }
    const q = query.toUpperCase();
    const results = coins.filter(c => !c.symbol.includes("_") && (c.symbol.toUpperCase().includes(q) || c.name.toUpperCase().includes(q))).slice(0, 8);
    setFiltered(results);
    setOpen(results.length > 0);
  }, [query, coins]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (coin) => {
    setQuery(coin.symbol.toUpperCase());
    setOpen(false);
    onSelect(coin.symbol.toUpperCase(), coin.name);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        style={styles.input}
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); }}
        placeholder="Search coin (e.g. XRP, BTC)"
        autoComplete="off"
      />
      {open && (
        <div style={styles.dropdown}>
          {filtered.map(coin => (
            <div key={coin.symbol} style={styles.dropdownItem} onMouseDown={() => handleSelect(coin)}>
              <span style={styles.dropdownSymbol}>{coin.symbol.toUpperCase()}</span>
              <span style={styles.dropdownName}>{coin.name}</span>
              <span style={styles.dropdownPrice}>${formatPrice(coin.price)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [holdings, setHoldings] = useState([
    { coin_symbol: "XRP", coin_name: "XRP", amount: "", average_buy_price: "", buy_date: "" }
  ]);

  const [goals, setGoals] = useState({
    target_net_worth: '', target_year: '', monthly_dca: '', risk_tolerance: 'medium'
  });

  const [exitLadder, setExitLadder] = useState([
    { target_price: '', sell_percentage: '', is_moon_bag: false }
  ]);

  const [tax, setTax] = useState({
    filing_status: 'single', income_bracket: '22', state_of_residence: ''
  });

  const addHolding = () => setHoldings([...holdings, { coin_symbol: "", coin_name: "", amount: "", average_buy_price: "", buy_date: "" }]);
  const removeHolding = (i) => setHoldings(holdings.filter((_, idx) => idx !== i));
  const updateHolding = (i, field, value) => {
    const updated = [...holdings];
    updated[i][field] = value;
    setHoldings(updated);
  };

  const addExitRung = () => setExitLadder([...exitLadder, { target_price: '', sell_percentage: '', is_moon_bag: false }]);
  const removeExitRung = (i) => setExitLadder(exitLadder.filter((_, idx) => idx !== i));
  const updateExitRung = (i, field, value) => {
    const updated = [...exitLadder];
    updated[i][field] = value;
    setExitLadder(updated);
  };

  const saveHoldings = async () => {
    setLoading(true); setError('');
    try {
      for (const h of holdings) {
        if (h.amount && h.average_buy_price && h.coin_symbol) {
          const res = await axios.post(`${API}/api/holdings`, h);
          if (h.buy_date) {
            await axios.post(`${API}/api/holdings/lots`, {
              holding_id: res.data.holding.id,
              coin_symbol: h.coin_symbol,
              amount: h.amount,
              buy_price: h.average_buy_price,
              buy_date: h.buy_date
            });
          }
        }
      }
      setStep(1);
    } catch (err) {
      setError('Failed to save holdings. Please try again.');
    } finally { setLoading(false); }
  };

  const saveGoals = async () => {
    setLoading(true); setError("");
    const missing = [];
    if (!goals.target_net_worth) missing.push("Target net worth");
    if (!goals.target_year) missing.push("Target year");
    if (missing.length > 0) { setError("Please fill in: " + missing.join(", ")); setLoading(false); return; }
    try {
      await axios.post(`${API}/api/wealth`, {...goals, monthly_dca: goals.monthly_dca || 0});
      setStep(2);
    } catch (err) {
      setError("Failed to save goals. Please try again.");
    } finally { setLoading(false); }
  };

  const saveExitLadder = async () => {
    setLoading(true); setError('');
    try {
      for (const rung of exitLadder) {
        if (rung.target_price && rung.sell_percentage) {
          await axios.post(`${API}/api/exit-ladder`, {
            coin_symbol: holdings[0]?.coin_symbol || 'XRP',
            target_price: rung.target_price,
            sell_percentage: rung.sell_percentage,
            is_moon_bag: rung.is_moon_bag
          });
        }
      }
      setStep(3);
    } catch (err) {
      setError('Failed to save exit ladder. Please try again.');
    } finally { setLoading(false); }
  };

  const saveTax = async () => {
    if (!tax.state_of_residence) { setError('Please select your state of residence.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/api/wealth`, { ...goals, ...tax, monthly_dca: goals.monthly_dca || 0, target_net_worth: goals.target_net_worth || 0, target_year: goals.target_year || 0 });
      setStep(4);
    } catch (err) {
      setError('Failed to save tax info. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <a href="/" style={styles.logo}>CryptoFolio <span style={styles.logoAccent}>CFO</span></a>
        <div style={styles.progressBar}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ ...styles.progressStep, background: i <= step ? '#1a6b4a' : '#e5e7eb' }} />
          ))}
        </div>
        <p style={styles.stepLabel}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        {error && <div style={styles.error}>{error}</div>}

        {step === 0 && (
          <div>
            <h2 style={styles.title}>What do you currently hold?</h2>
            <p style={styles.sub}>Search and add your crypto holdings. You can edit these anytime.</p>
            {holdings.map((h, i) => (
              <div key={i} style={styles.holdingRow}>
                <div style={{...styles.holdingGrid, gridTemplateColumns: window.innerWidth < 600 ? "1fr 1fr" : "1fr 1fr 1fr 1fr"}}>
                  <div style={styles.field}>
                    <label style={styles.label}>Coin</label>
                    <CoinSearch
                      value={h.coin_symbol}
                      onChange={(val) => updateHolding(i, 'coin_symbol', val)}
                      onSelect={(symbol, name) => {
                        updateHolding(i, 'coin_symbol', symbol);
                        updateHolding(i, 'coin_name', name);
                      }}
                    />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Amount</label>
                    <input style={styles.input} type="number" value={h.amount} onChange={e => updateHolding(i, "amount", e.target.value)} placeholder="10000" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Avg buy price ($)</label>
                    <input style={styles.input} type="number" value={h.average_buy_price} onChange={e => updateHolding(i, "average_buy_price", e.target.value)} placeholder="0.80" step="0.01" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Purchase date</label>
                    <input style={styles.input} type="date" value={h.buy_date} onChange={e => updateHolding(i, "buy_date", e.target.value)} />
                  </div>
                </div>
                {i > 0 && <button onClick={() => removeHolding(i)} style={styles.removeBtn}>Remove</button>}
              </div>
            ))}
            <button onClick={addHolding} style={styles.addBtn}>+ Add another coin</button>
            <button onClick={saveHoldings} style={styles.btn} disabled={loading}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 style={styles.title}>What are you trying to achieve?</h2>
            <p style={styles.sub}>Set your wealth goal so we can track your progress.</p>
            <div style={styles.field}>
              <label style={styles.label}>Target net worth ($)</label>
              <input style={styles.input} type="number" value={goals.target_net_worth} onChange={e => setGoals({...goals, target_net_worth: e.target.value})} placeholder="1000000" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Target year</label>
              <input style={styles.input} type="number" value={goals.target_year} onChange={e => setGoals({...goals, target_year: e.target.value})} placeholder="2030" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Monthly DCA amount ($)</label>
              <input style={styles.input} type="number" value={goals.monthly_dca} onChange={e => setGoals({...goals, monthly_dca: e.target.value})} placeholder="500" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Risk tolerance</label>
              <select style={styles.input} value={goals.risk_tolerance} onChange={e => setGoals({...goals, risk_tolerance: e.target.value})}>
                <option value="low">Low — I want to be conservative</option>
                <option value="medium">Medium — Balanced approach</option>
                <option value="high">High — I can handle volatility</option>
              </select>
            </div>
            <button onClick={() => { setStep(0); setError(''); }} style={{background:"transparent",color:"#6b6b6b",border:"1px solid rgba(0,0,0,0.12)",padding:"12px",borderRadius:"8px",fontSize:"15px",cursor:"pointer",marginBottom:"8px",width:"100%"}}>Back</button>
            <button onClick={saveGoals} style={styles.btn} disabled={loading}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={styles.title}>How do you want to get out?</h2>
            <p style={styles.sub}>Build your sell ladder for {holdings[0]?.coin_symbol || 'XRP'}. Set the price and percentage to sell at each level.</p>
            {exitLadder.map((rung, i) => (
              <div key={i} style={styles.holdingRow}>
                <div style={styles.holdingGrid}>
                  <div style={styles.field}>
                    <label style={styles.label}>Target price ($)</label>
                    <input style={styles.input} type="number" value={rung.target_price} onChange={e => updateExitRung(i, 'target_price', e.target.value)} placeholder="5.00" step="0.01" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Sell percentage (%)</label>
                    <input style={styles.input} type="number" value={rung.sell_percentage} onChange={e => updateExitRung(i, 'sell_percentage', e.target.value)} placeholder="10" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Moon bag?</label>
                    <select style={styles.input} value={rung.is_moon_bag} onChange={e => updateExitRung(i, 'is_moon_bag', e.target.value === 'true')}>
                      <option value="false">No — sell at target</option>
                      <option value="true">Yes — never sell this</option>
                    </select>
                  </div>
                </div>
                {i > 0 && <button onClick={() => removeExitRung(i)} style={styles.removeBtn}>Remove</button>}
              </div>
            ))}
            <button onClick={addExitRung} style={styles.addBtn}>+ Add another rung</button>
            <button onClick={() => { setStep(1); setError(''); }} style={{background:"transparent",color:"#6b6b6b",border:"1px solid rgba(0,0,0,0.12)",padding:"12px",borderRadius:"8px",fontSize:"15px",cursor:"pointer",marginBottom:"8px",width:"100%"}}>Back</button>
            <button onClick={saveExitLadder} style={styles.btn} disabled={loading}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={styles.title}>What's your tax situation?</h2>
            <p style={styles.sub}>This helps us calculate accurate estimates. We never share this data.</p>
            <div style={styles.field}>
              <label style={styles.label}>Filing status</label>
              <select style={styles.input} value={tax.filing_status} onChange={e => setTax({...tax, filing_status: e.target.value})}>
                <option value="single">Single</option>
                <option value="married_joint">Married filing jointly</option>
                <option value="married_separate">Married filing separately</option>
                <option value="head_of_household">Head of household</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Federal tax bracket</label>
              <select style={styles.input} value={tax.income_bracket} onChange={e => setTax({...tax, income_bracket: e.target.value})}>
                <option value="10">10%</option>
                <option value="12">12%</option>
                <option value="22">22%</option>
                <option value="24">24%</option>
                <option value="32">32%</option>
                <option value="35">35%</option>
                <option value="37">37%</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>State of residence</label>
              <select style={styles.input} value={tax.state_of_residence} onChange={e => setTax({...tax, state_of_residence: e.target.value})}>
                <option value="">Select your state...</option>
                {["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <p style={styles.fieldNote}>Currently optimized for US tax residents. International support coming soon.</p>
            </div>
            <button onClick={() => { setStep(2); setError(''); }} style={{background:"transparent",color:"#6b6b6b",border:"1px solid rgba(0,0,0,0.12)",padding:"12px",borderRadius:"8px",fontSize:"15px",cursor:"pointer",marginBottom:"8px",width:"100%"}}>Back</button>
            <button onClick={saveTax} style={styles.btn} disabled={loading}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        )}

        {step === 4 && (
          <div style={{textAlign:'center'}}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>You're all set!</h2>
            <p style={styles.sub}>Your financial plan is ready. Your dashboard is personalized and waiting for you.</p>
            <button onClick={() => navigate('/dashboard?from=onboarding')} style={styles.btn}>
              Go to my dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
